import React, { useState, useEffect } from 'react';
import { getAuth } from 'firebase/auth';
import { db } from '../firebase';
import { collection, addDoc, getDocs, query, where, doc, getDoc } from 'firebase/firestore';
import { fetchInterestStatus } from '../utils/interestUtils';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ParentAssessmentsCard from '../components/ParentAssessmentsCard';
import Spinner from '../components/Spinner';
import { FaSearch, FaPlusCircle, FaChevronDown, FaChevronUp } from 'react-icons/fa';

const ROLES = require('../components/roles').ROLES;

const ParentsHome = () => {
  // --- User and Profile State ---
  const [user, setUser] = useState(null);
  const [parentProfile, setParentProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  // --- Search Card State ---
  const [search, setSearch] = useState({ role: '', specialty: '', location: '' });
  const [searchResults, setSearchResults] = useState([]);
  const [interestStatusMap, setInterestStatusMap] = useState({}); // { [professionalId]: {status, ...} }
  const [searching, setSearching] = useState(false);
  const [waitingResponse, setWaitingResponse] = useState({});
  let navigate;
  if (typeof window !== 'undefined' && window.location) {
    navigate = (path) => window.location.assign(path);
  } else {
    try {
      const reactRouterDom = require('react-router-dom');
      navigate = reactRouterDom.useNavigate();
    } catch (e) {
      console.warn('useNavigate not available, falling back to window.location.assign');
      navigate = (path) => { if (typeof window !== 'undefined') window.location.assign(path); };
    }
  }

  // --- New Requirement Form State ---
  const [showModal, setShowModal] = useState(false);
  const [showOptionalFields, setShowOptionalFields] = useState(false);
  const [newReq, setNewReq] = useState({
    title: '', type: '', date: '', age: '', gender: '', city: '', institution: '', description: '', skillsNeeded: ''
  });
  const [postingRequirement, setPostingRequirement] = useState(false);

  // --- My Jobs/Requests State ---
  const [myJobs, setMyJobs] = useState([]);
  const [fetchingJobs, setFetchingJobs] = useState(false);

  const [toast, setToast] = useState(null);

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = auth.onAuthStateChanged(async (firebaseUser) => {
      setLoading(true);
      if (firebaseUser) {
        setUser(firebaseUser);
        try {
          const userDocRef = doc(db, "users", firebaseUser.uid);
          const userDocSnap = await getDoc(userDocRef);
          if (userDocSnap.exists()) {
            setParentProfile(userDocSnap.data());
          } else {
            console.warn("Parent profile data not found in Firestore for UID:", firebaseUser.uid);
            setParentProfile({ displayName: firebaseUser.displayName || "User", membership: null });
          }
          await fetchMyJobs(firebaseUser.uid); // Pass UID to fetchMyJobs
        } catch (error) {
          console.error("Error fetching parent data or jobs:", error);
          setToast("Error loading dashboard data.");
          setParentProfile({ displayName: firebaseUser.displayName || "User", membership: null });
        }
      } else {
        setUser(null);
        setParentProfile(null);
        setMyJobs([]);
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  const fetchMyJobs = async (userId) => {
    if (!userId) return;
    setFetchingJobs(true);
    try {
      const q = query(collection(db, 'parentRequirements'), where('parentId', '==', userId), where('status', '!=', 'archived'));
      const querySnapshot = await getDocs(q);
      const jobs = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setMyJobs(jobs);
    } catch (error) {
      console.error("Error fetching my jobs:", error);
      setToast("Failed to load your posted requirements.");
    }
    setFetchingJobs(false);
  };

  const calculateTrialDaysRemaining = () => {
    if (!parentProfile || !parentProfile.membership || parentProfile.membership.status !== 'trialing' || !parentProfile.membership.trialEndDate) {
      return null;
    }
    const trialEnd = parentProfile.membership.trialEndDate.seconds
      ? new Date(parentProfile.membership.trialEndDate.seconds * 1000)
      : new Date(parentProfile.membership.trialEndDate);
    const now = new Date();
    const diffTime = trialEnd.getTime() - now.getTime();
    if (diffTime <= 0) return 0;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const trialDaysRemaining = calculateTrialDaysRemaining();

  const handleSearch = async (e) => {
    if (e) e.preventDefault();
    setSearching(true);
    setSearchResults([]);
    setInterestStatusMap({});
    setToast(null);

    try {
      // First get the professional documents from the users collection
      let professionalsRef = collection(db, 'users');
      let q = query(professionalsRef, where('role', '==', search.role));

      if (search.specialty) {
        q = query(professionalsRef, 
          where('role', '==', search.role), 
          where('specialties', 'array-contains', search.specialty)
        );
      }
      if (search.location) {
        if (search.specialty) {
          q = query(professionalsRef, 
            where('role', '==', search.role), 
            where('specialties', 'array-contains', search.specialty),
            where('city', '==', search.location)
          );
        } else {
          q = query(professionalsRef, 
            where('role', '==', search.role), 
            where('city', '==', search.location)
          );
        }
      }

      const querySnapshot = await getDocs(q);
      
      // Process results to include display names
      const results = await Promise.all(querySnapshot.docs.map(async (doc) => {
        const data = doc.data();
        
        // Try to get the user's display name from the users collection
        try {
          const userDoc = await getDoc(doc(db, 'users', doc.id));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            return { 
              id: doc.id, 
              ...data,
              // Use the best available name in this order:
              // 1. displayName from user doc
              // 2. name from user doc
              // 3. name from professional data
              // 4. email username as fallback
              displayName: userData.displayName || userData.name || data.name || data.email?.split('@')[0] || 'Professional'
            };
          }
        } catch (error) {
          console.error('Error fetching user data:', error);
        }
        
        // Fallback if we couldn't get user doc
        return { 
          id: doc.id, 
          ...data,
          displayName: data.name || data.email?.split('@')[0] || 'Professional'
        };
      }));
      
      setSearchResults(results);

      // Fetch interest status for each professional
      if (user && results.length > 0) {
        const statuses = {};
        await Promise.all(results.map(async (prof) => {
          const statusObj = await fetchInterestStatus(user.uid, prof.id, search.role);
          if (statusObj) statuses[prof.id] = statusObj;
        }));
        setInterestStatusMap(statuses);
      }

      if (results.length === 0) {
        setToast('No professionals found matching your criteria.');
      }
    } catch (error) {
      console.error('Error searching professionals:', error);
      setToast('Error searching for professionals. Please try again.');
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  };

  const roleLabel = (roleKey) => {
    const roleObj = ROLES.find(r => r.key === roleKey);
    return roleObj ? roleObj.label : roleKey;
  };

  const handleReqChange = (e) => {
    const { name, value } = e.target;
    setNewReq(prevReq => ({
      ...prevReq,
      [name]: value
    }));
  };

  const handleRequirementSubmit = async (e) => {
    e.preventDefault();
    if (!user) {
      setToast("You must be logged in to post a requirement.");
      return;
    }
    setPostingRequirement(true);
    setToast(null);
    try {
      await addDoc(collection(db, 'parentRequirements'), {
        ...newReq,
        parentId: user.uid,
        parentName: parentProfile?.displayName || user.displayName || 'Anonymous Parent',
        postedDate: new Date(),
        status: 'open', // Initial status
      });
      setToast("Requirement posted successfully!");
      setNewReq({ title: '', type: '', date: '', age: '', gender: '', city: '', institution: '', description: '', skillsNeeded: '' }); // Reset form
      setShowModal(false); // Close modal if it's open
      await fetchMyJobs(user.uid); // Refresh the list of jobs
    } catch (error) {
      console.error("Error posting requirement:", error);
      setToast("Failed to post requirement. Please try again.");
    } finally {
      setPostingRequirement(false);
    }
  };

  const handleExpressInterest = async (professional, searchRole) => {
    if (!user) {
      setToast('Please log in to express interest.');
      return;
    }
    // Always allow storing interest, but show a toast if not premium
    const notPremium = !parentProfile?.membership?.status || (parentProfile.membership.status !== 'active_premium' && parentProfile.membership.status !== 'trialing');
    if (notPremium) {
      setToast('Interest sent! Upgrade to Premium to unlock full contact and priority matching.');
    }
    setWaitingResponse(prev => ({ ...prev, [professional.id]: true }));
    try {
      // Example: Add to an 'interests' collection
      await addDoc(collection(db, 'interests'), {
        parentId: user.uid,
        parentName: parentProfile?.displayName || user.displayName,
        professionalId: professional.id,
        professionalName: professional.name || professional.fullName || professional.orgName,
        professionalRole: professional.role,
        searchedRole: searchRole, // The role parent searched for
        status: 'pending_professional_response', // Initial status
        timestamp: new Date(),
        parentMessage: `Interested in your ${roleLabel(professional.role)} services in ${professional.city || 'your area'}.`
      });
    } catch (error) {
      console.error('Error expressing interest:', error);
      setToast('Could not express interest. Please try again.');
    } finally {
      // Keep waiting indicator for a bit to give feedback, then remove
      setTimeout(() => setWaitingResponse(prev => ({ ...prev, [professional.id]: false })), 3000);
    }
  };

  const handleUnlock = (professional) => {
    // This should check membership and then navigate or show details
    if (!parentProfile?.membership?.status || (parentProfile.membership.status !== 'active_premium' && parentProfile.membership.status !== 'trialing')) {
      setToast('Please upgrade to Premium to unlock full contact details.');
      setTimeout(() => navigate('/membership'), 1500);
      return;
    }
    // If premium, ideally you'd fetch full details or store them differently.
    // For now, let's just log a message and potentially redirect to a profile page if one exists.
    setToast('Premium feature: Unlocking full details (placeholder).');
    // Example: navigate(`/professional/${professional.id}`);
  };

  if (loading) return <div className="flex justify-center items-center h-screen"><Spinner /></div>;

  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-100 via-indigo-50 to-purple-100 font-sans">
      <Header />
      {toast && (
        <div className="fixed top-20 right-5 bg-blue-500 text-white px-6 py-3 rounded-lg shadow-lg z-50 animate-pulse">
          {toast}
        </div>
      )}

      {user && parentProfile && (
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-white shadow-xl rounded-xl p-6 border border-slate-200 mb-8">
            <h1 className="text-3xl font-bold text-gray-800 mb-2">
              Welcome back, {parentProfile.displayName || user.displayName || 'Parent'}!
            </h1>
            <p className="text-lg text-gray-600 mb-4">Let’s find the right expert for your child today.</p>

            {parentProfile.membership && parentProfile.membership.status === 'trialing' && trialDaysRemaining !== null && (
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between bg-yellow-50 border border-yellow-300 p-4 rounded-lg mb-4">
                <div>
                  <p className="text-yellow-700 font-semibold">
                    You're on a 3-month free plan.
                    {trialDaysRemaining > 0 ?
                      `${trialDaysRemaining} day${trialDaysRemaining > 1 ? 's' : ''} remaining.` :
                      "Your trial has expired."
                    }
                  </p>
                  {trialDaysRemaining > 0 && (
                    <p className="text-sm text-yellow-600">
                      Unlock more expert contacts with Premium Membership.
                    </p>
                  )}
                </div>
                <button
                  className="mt-3 sm:mt-0 sm:ml-4 bg-gradient-to-r from-purple-600 to-pink-500 text-white px-5 py-2.5 rounded-md shadow-md hover:scale-105 transition-transform duration-150 ease-in-out font-semibold whitespace-nowrap"
                  onClick={() => navigate('/membership')}
                >
                  Unlock Premium Access
                </button>
              </div>
            )}
            {(!parentProfile.membership || parentProfile.membership.status !== 'active_premium') && !(parentProfile.membership && parentProfile.membership.status === 'trialing' && trialDaysRemaining > 0) && (
              <div className="mt-4 text-center sm:text-left">
                <button
                  className="bg-gradient-to-r from-orange-500 to-red-500 text-white px-6 py-3 rounded-lg shadow-lg hover:from-orange-600 hover:to-red-600 transition-all duration-150 ease-in-out transform hover:scale-105 font-semibold"
                  onClick={() => navigate('/membership')}
                >
                  Upgrade to Premium & Unlock All Contacts!
                </button>
                <p className="text-sm text-gray-500 mt-2">First 3 months free for new members!</p>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
          <div className="bg-white shadow-md rounded-xl p-6 mb-6 border border-slate-200">
            <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
              <FaSearch className="mr-3 text-indigo-600" /> Search for Professionals
            </h2>
            <form onSubmit={handleSearch} className="space-y-6">
              <div className="flex-1 min-w-[180px]">
                <label className="block text-base font-semibold text-gray-800 mb-1">Role</label>
                <select name="role" className="mt-1 block w-full rounded-lg border border-gray-400 bg-white px-3 py-2 text-gray-900 shadow-sm" value={search.role} onChange={e => setSearch(s => ({ ...s, role: e.target.value }))} required>
                  <option value="">Select...</option>
                  {ROLES.map(r => (
                    <option key={r.key} value={r.key}>{r.label}</option>
                  ))}
                </select>
              </div>
              <div className="flex-1 min-w-[180px]">
                <label className="block text-base font-semibold text-gray-800 mb-1">Specialty</label>
                <input name="specialty" className="mt-1 block w-full rounded-lg border border-gray-400 bg-white px-3 py-2 text-gray-900 shadow-sm" value={search.specialty} onChange={e => setSearch(s => ({ ...s, specialty: e.target.value }))} placeholder="e.g. Speech, OT" />
              </div>
              <div className="flex-1 min-w-[180px]">
                <label className="block text-base font-semibold text-gray-800 mb-1">Location (City)</label>
                <input name="location" className="mt-1 block w-full rounded-lg border border-gray-400 bg-white px-3 py-2 text-gray-900 shadow-sm" value={search.location} onChange={e => setSearch(s => ({ ...s, location: e.target.value }))} placeholder="e.g. Hyderabad" />
              </div>
              <div className="flex items-end mt-2">
                <button type="submit" className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-2 rounded font-semibold min-w-[120px]">{searching ? 'Searching...' : 'Search'}</button>
              </div>
            </form>
            {searching ? (
              <div className="py-8 text-center text-indigo-700 font-semibold">Searching...</div>
            ) : searchResults && searchResults.length > 0 ? (
              <div className="overflow-x-auto mt-6">
                <table className="min-w-full divide-y divide-gray-200 bg-indigo-50 rounded-xl">
                  <thead>
                    <tr>
                      <th className="px-4 py-3 text-left text-base font-bold text-indigo-900">Name</th>
                      <th className="px-4 py-3 text-left text-base font-bold text-indigo-900">Role</th>
                      <th className="px-4 py-3 text-left text-base font-bold text-indigo-900">City</th>
                      <th className="px-4 py-3 text-left text-base font-bold text-indigo-900">Specialty</th>
                      <th className="px-4 py-3 text-left text-base font-bold text-indigo-900">Experience</th>
                      <th className="px-4 py-3 text-left text-base font-bold text-indigo-900">Contact</th>
                      <th className="px-4 py-3 text-center text-base font-bold text-indigo-900">Express Interest</th>
                      <th className="px-4 py-3 text-center text-base font-bold text-indigo-900">Unlock</th>
                    </tr>
                  </thead>
                  <tbody>
                    {searchResults.map((res, idx) => {
                      const mask = (str, visible = 2) => str ? str.slice(0, visible) + '****' : '-';
                      const displayName = res.displayName || 'Professional';
                      const mobile = res.mobile || res.phone || '-';
                      const email = res.email || '-';
                      const maskedMobile = mobile && mobile !== '-' ? mask(mobile, 2) : '-';
                      const maskedEmail = email && email !== '-' ? mask(email, 2) + '@***' : '-';
                      const isWaiting = waitingResponse[res.id];
                      const interest = interestStatusMap[res.id];
                      const status = interest?.status;
                      // If accepted, show full contact
                      const showContact = status === 'accepted';
                      // If waiting for professional, disable everything
                      const isPending = status === 'pending_professional_response';
                      return (
                        <tr key={res.id} className={`border-b border-indigo-100 hover:bg-indigo-100/50 transition ${isPending ? 'opacity-60' : ''}`}>
                          <td className="px-4 py-3 font-semibold text-indigo-900">{displayName}</td>
                          <td className="px-4 py-3">{roleLabel(res.role)}</td>
                          <td className="px-4 py-3">{res.city || '-'}</td>
                          <td className="px-4 py-3">{res.specialty || '-'}</td>
                          <td className="px-4 py-3">{res.experience ? (Array.isArray(res.experience) ? res.experience.length : res.experience) : '-' } yrs</td>
                          <td className="px-4 py-3">
                            <div className="flex flex-col">
                              <span className="font-medium">{showContact ? displayName : mask(displayName, 1)}</span>
                              <span className="text-xs text-gray-500">{showContact ? mobile : maskedMobile}</span>
                              <span className="text-xs text-gray-500">{showContact ? email : maskedEmail}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <button
                              className={`px-4 py-2 rounded font-semibold w-full ${isWaiting || isPending ? 'bg-gray-400 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700 text-white'}`}
                              disabled={isWaiting || isPending || !!status}
                              onClick={() => handleExpressInterest(res, search.role)}
                            >
                              {isPending ? 'Waiting Response' : status === 'accepted' ? 'Accepted' : isWaiting ? 'Waiting...' : 'Express Interest'}
                            </button>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <button
                              className="px-4 py-2 rounded font-semibold w-full bg-indigo-600 hover:bg-indigo-700 text-white"
                              onClick={() => navigate('/membership')}
                            >Unlock</button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : searchResults && searchResults.length === 0 && search.role ? (
              <div className="py-6 text-gray-500 text-center">No professionals or organizations found. Try adjusting your filters.</div>
            ) : null}
          </div>

          <div className="bg-white shadow-md rounded-xl p-6 mb-6 border border-slate-200">
            <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
              <FaPlusCircle className="mr-3 text-green-600" /> Post a New Requirement
            </h2>
            <form onSubmit={handleRequirementSubmit} className="space-y-6">
              <div className="flex-1 min-w-[180px]">
                <label className="block text-base font-semibold text-gray-800 mb-1">Title <span title="What do you need?" className="ml-1 text-gray-400 cursor-help">&#9432;</span></label>
                <input name="title" value={newReq.title} onChange={handleReqChange} required className="mt-1 block w-full rounded-lg border border-gray-400 bg-white px-3 py-2 text-gray-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition" placeholder="e.g. Need a Speech Therapist" />
              </div>
              <div className="flex-1 min-w-[180px]">
                <label className="block text-base font-semibold text-gray-800 mb-1">Service <span title="Select the type of professional or service" className="ml-1 text-gray-400 cursor-help">&#9432;</span></label>
                <select
                  name="type"
                  className="mt-1 block w-full rounded-lg border border-gray-400 bg-white px-3 py-2 text-gray-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition"
                  style={{ fontWeight: 600 }}
                  value={newReq.type}
                  onChange={handleReqChange}
                  required
                >
                  <option value="">Select...</option>
                  {ROLES.map(r => (
                    <option key={r.key} value={r.key}>{r.label}</option>
                  ))}
                </select>
              </div>
              <div className="flex-1 min-w-[180px]">
                <label className="block text-base font-semibold text-gray-800 mb-1">Date <span title="When do you need this?" className="ml-1 text-gray-400 cursor-help">&#9432;</span></label>
                <input name="date" type="date" value={newReq.date} onChange={handleReqChange} required className="mt-1 block w-full rounded-lg border border-gray-400 bg-white px-3 py-2 text-gray-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition" />
              </div>
              <div className="flex-1 min-w-[180px]">
                <label className="block text-base font-semibold text-gray-800 mb-1">City <span title="Your location" className="ml-1 text-gray-400 cursor-help">&#9432;</span></label>
                <select name="city" value={newReq.city} onChange={handleReqChange} className="mt-1 block w-full rounded-lg border border-gray-400 bg-white px-3 py-2 text-gray-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition">
  <option value="">Select City</option>
  <option value="Hyderabad">Hyderabad</option>
  <option value="Bangalore">Bangalore</option>
  <option value="Chennai">Chennai</option>
  <option value="Mumbai">Mumbai</option>
  <option value="Delhi">Delhi</option>
  <option value="Other">Other</option>
</select>
              </div>
              <div className="flex-1 min-w-[180px]">
                <label className="block text-base font-semibold text-gray-800 mb-1">Description <span title="Describe what support you’re looking for" className="ml-1 text-gray-400 cursor-help">&#9432;</span></label>
                <textarea name="description" value={newReq.description} onChange={handleReqChange} className="mt-1 block w-full rounded-lg border border-gray-400 bg-white px-3 py-2 text-gray-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition" placeholder="Describe what support you’re looking for..." />
              </div>
              <div className="w-full">
                <button type="button" onClick={() => setShowOptionalFields(v => !v)} className="text-indigo-700 font-semibold flex items-center gap-1 mt-2 mb-1 focus:outline-none">
                  <span>{showOptionalFields ? 'Hide' : 'More Options'}</span>
                  <svg className={`w-4 h-4 transform transition-transform duration-200 ${showOptionalFields ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" /></svg>
                </button>
                {showOptionalFields && (
                  <div className="overflow-hidden transition-all duration-300 ease-in-out max-h-60 opacity-100">
                    <div className="flex flex-wrap gap-4 mt-2">
                      <div className="flex-1 min-w-[100px]">
                        <label className="block text-base font-semibold text-gray-800 mb-1">Age <span title="Optional" className="text-xs text-gray-400">(optional)</span></label>
                        <select name="age" value={newReq.age} onChange={handleReqChange} className="mt-1 block w-full rounded-lg border border-gray-400 bg-white px-3 py-2 text-gray-900 shadow-sm">
  <option value="">Select Age</option>
  {Array.from({length: 18}, (_, i) => <option key={i+1} value={i+1}>{i+1}</option>)}
</select>
                      </div>
                      <div className="flex-1 min-w-[100px]">
                        <label className="block text-base font-semibold text-gray-800 mb-1">Gender <span title="Optional" className="text-xs text-gray-400">(optional)</span></label>
                        <select name="gender" value={newReq.gender} onChange={handleReqChange} className="mt-1 block w-full rounded-lg border border-gray-400 bg-white px-3 py-2 text-gray-900 shadow-sm">
  <option value="">Select Gender</option>
  <option value="Male">Male</option>
  <option value="Female">Female</option>
  <option value="Other">Other</option>
</select>
                      </div>
                      <div className="flex-1 min-w-[180px]">
                        <label className="block text-base font-semibold text-gray-800 mb-1">Institution <span title="Optional" className="text-xs text-gray-400">(optional)</span></label>
                        <select name="institution" value={newReq.institution} onChange={handleReqChange} className="mt-1 block w-full rounded-lg border border-gray-400 bg-white px-3 py-2 text-gray-900 shadow-sm">
  <option value="">Select Institution</option>
  <option value="School">School</option>
  <option value="Center">Center</option>
  <option value="Clinic">Clinic</option>
  <option value="Home">Home</option>
  <option value="Other">Other</option>
</select>
                      </div>
                      <div className="flex-1 min-w-[180px]">
                        <label className="block text-base font-semibold text-gray-800 mb-1">Skills Needed <span title="Optional" className="text-xs text-gray-400">(optional)</span></label>
                        <input name="skillsNeeded" value={newReq.skillsNeeded} onChange={handleReqChange} className="mt-1 block w-full rounded-lg border border-gray-400 bg-white px-3 py-2 text-gray-900 shadow-sm" placeholder="e.g. Speech, OT" />
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <div className="flex items-end gap-2 mt-4 w-full justify-end">
                <button type="reset" className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-4 py-2 rounded" onClick={e => { e.preventDefault(); setNewReq({ title: '', type: '', date: '', age: '', gender: '', city: '', institution: '', description: '', skillsNeeded: '' }); }}>Cancel</button>
                <button type="submit" className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-2 rounded font-semibold">Save</button>
              </div>
            </form>
          </div>
        </div>

        <div className="my-8 max-w-4xl mx-auto bg-gradient-to-b from-indigo-50 to-white rounded-2xl shadow-lg p-8 border border-gray-300">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-bold text-indigo-800 flex items-center gap-2">
              <span className="inline-block text-indigo-500"><svg xmlns='http://www.w3.org/2000/svg' className='h-7 w-7' fill='none' viewBox='0 0 24 24' stroke='currentColor'><path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M9 17v-2a2 2 0 012-2h2a2 2 0 012 2v2m-6 0a2 2 0 002 2h2a2 2 0 002-2m-6 0V7a2 2 0 012-2h2a2 2 0 012 2v10m-6 0h6' /></svg></span>
              My Previous Requests
            </h2>
            <span className="text-xs bg-indigo-100 text-indigo-700 px-3 py-1 rounded-full font-semibold">
              {myJobs.length} total
            </span>
          </div>
          {myJobs.length === 0 ? (
            <div className="text-gray-400 text-base font-medium py-8 text-center">You have not posted any requirements yet.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-indigo-200">
                <thead>
                  <tr>
                    <th className="px-4 py-2 text-left text-base font-bold text-indigo-700 bg-indigo-50">Type</th>
                    <th className="px-4 py-2 text-left text-base font-bold text-indigo-700 bg-indigo-50">Date</th>
                    <th className="px-4 py-2 text-left text-base font-bold text-indigo-700 bg-indigo-50">Location</th>
                    <th className="px-4 py-2 text-left text-base font-bold text-indigo-700 bg-indigo-50">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {myJobs.map(job => (
                    <tr key={job.id} className="hover:bg-indigo-100 border-b border-indigo-100 transition">
                      <td className="px-4 py-2 font-semibold text-indigo-900">{job.type}</td>
                      <td className="px-4 py-2">{job.date}</td>
                      <td className="px-4 py-2">{job.city}</td>
                      <td className="px-4 py-2">{job.status || 'Open'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
      <ParentAssessmentsCard user={user} />
      <Footer />
    </div>
  );
};

export default ParentsHome;
